import {
  Accessor,
  createContext,
  createSignal,
  JSX,
  useContext,
} from "solid-js";
import Button, { ButtonProps } from "./components/button";
import LinkButton, { LinkButtonProps } from "./components/link_button";
import Input from "./components/input";
import Tabs from "./components/tabs";
import Toggle, { ToggleProps } from "./components/toggle";
import SegmentedControl, {
  SegmentedControlProps,
} from "./components/segmented-control";
import Checkbox, { CheckboxProps } from "./components/checkbox";
import Loader, { LoaderProps } from "./components/loader";
import CircularLoader, { CircularLoaderProps } from "./components/circular-loader";

export type TelescopeTheme =
  | "telescope-light-theme"
  | "telescope-dark-theme"
  | "axis-bank-burgundy-theme";

type TelescopeContextType = {
  theme: Accessor<TelescopeTheme>;
  toggleTheme: () => void;
  setTheme: (theme: TelescopeTheme) => void;
  components: {
    Button: (props: ButtonProps) => JSX.Element;
    LinkButton: (props: LinkButtonProps) => JSX.Element;
    Input: typeof Input;
    Tab: typeof Tabs;
    Toggle: (props: ToggleProps) => JSX.Element;
    SegmentedControl: (props: SegmentedControlProps) => JSX.Element;
    Checkbox: (props: CheckboxProps) => JSX.Element;
    Loader: (props: LoaderProps) => JSX.Element;
    CircularLoader: (props: CircularLoaderProps) => JSX.Element;
  };
};

const TelescopeContext = createContext<TelescopeContextType>();

export function useTelescopeContext() {
  const context = useContext(TelescopeContext);
  if (!context) {
    throw new Error(
      "useTelescopeContext must be used within a TelescopeProvider"
    );
  }
  return context;
}

export function TelescopeProvider(props: {
  children: JSX.Element;
  theme?: TelescopeTheme;
  components?: {
    Button?: (props: ButtonProps) => JSX.Element;
    LinkButton?: (props: LinkButtonProps) => JSX.Element;
    Input?: typeof Input;
    Tab?: typeof Tabs;
    Toggle?: (props: ToggleProps) => JSX.Element;
    SegmentedControl?: (props: SegmentedControlProps) => JSX.Element;
    Checkbox?: (props: CheckboxProps) => JSX.Element;
    Loader?: (props: LoaderProps) => JSX.Element;
    CircularLoader?: (props: CircularLoaderProps) => JSX.Element;
  };
}) {
  const [theme, setTheme] = createSignal(
    props.theme ?? "telescope-light-theme"
  );

  const toggleTheme = () => {
    setTheme(
      theme() === "telescope-light-theme"
        ? "telescope-dark-theme"
        : "telescope-light-theme"
    );
  };

  return (
    <TelescopeContext.Provider
      value={{
        toggleTheme,
        theme,
        setTheme,
        components: {
          Button: props.components?.Button ?? Button,
          LinkButton: props.components?.LinkButton ?? LinkButton,
          Input: props.components?.Input ?? Input,
          Tab: props.components?.Tab ?? Tabs,
          Toggle: props.components?.Toggle ?? Toggle,
          SegmentedControl:
            props.components?.SegmentedControl ?? SegmentedControl,
          Checkbox: props.components?.Checkbox ?? Checkbox,
          Loader: props.components?.Loader ?? Loader,
          CircularLoader: props.components?.CircularLoader ?? CircularLoader,
        },
      }}
    >
      <>
        <style>
          {`
          .theme-container * {
            transition: all 300ms;
          }
        `}
        </style>
        <div
          class="theme-container h-full w-full"
          classList={{
            [theme()]: true,
          }}
        >
          {props.children}
        </div>
      </>
    </TelescopeContext.Provider>
  );
}

export default TelescopeContext;
